/**
 * onboardingInstance.js — shared builder for onboardingInstances documents.
 *
 * SINGLE SOURCE OF TRUTH for how an onboarding instance is assembled from a
 * template: item flattening + per-variant instance payload. Three call sites
 * use it:
 *   - 'pipeline'      — admin OnboardingPipeline "Start Onboarding" modal
 *   - 'self-service'  — agent OnboardingFlow profile-completion auto-assign
 *   - 'auto-provision'— server-side auto-assign at user creation (RW-697)
 *
 * The server has a CommonJS twin at `functions/src/util/onboardingInstance.js`
 * (Cloud Functions can't import client `src/`). Keep the two in sync — same
 * flatten logic, same payload shapes. If you edit one, edit the other.
 *
 * This module is PURE: no firebase imports. Timestamps and any SDK-resolved
 * values (serverTimestamp() sentinel, Timestamp.now(), Timestamp.fromDate())
 * are injected by the caller so the builder stays unit-testable.
 *
 * The 'pipeline' and 'self-service' variants reproduce the two pre-existing
 * inline creators byte-for-byte (RW-697 Phase 2 Step 1 is behavior-preserving).
 *
 * RW-1632: office-override lookups canonicalize the office key on both sides
 * (templates store display names, user docs store id slugs) and tag the items
 * they contribute with `officeOverrideOf` so a later office change can retire
 * them (T2-14). Non-array override entries (the retired seed placeholder
 * shape, T2-08) are ignored instead of crashing the assign transaction.
 * The 'auto-provision' variant is derived from what the agent-facing
 * OnboardingChecklist reader actually consumes (items[] grouped by
 * phaseId||sectionId, tenantId, startedAt, status!=='complete'), plus the
 * admin-view extras (office, counts) and the kept side-effect contract
 * (onboardingInstanceId update + activity log + createdBy:'auto-provision').
 */

import { canonicalOfficeKey, officeOverridesFor } from './officeMismatch';

// ─── instance lifecycle predicates (RW-2065) ────────────────────────────────
//
// Before RW-2065 there was exactly ONE lifecycle axis — `status` — and every
// consumer hand-wrote `status !== 'complete'` to mean "this hire is live".
// That worked only because a hire had two possible ends: still going, or
// finished. Unassigning a hire adds a third (archived), and 20+ hand-written
// predicates cannot each be taught about it reliably — the ones that get
// missed keep an archived hire blocking assignment, inflating "active hires",
// or showing the agent a checklist nobody expects them to do.
//
// So the axis gets a name, in ONE place, twinned client↔server. Read these as:
//   archived  — staff took them off onboarding; the record is retained (Rule
//               23 soft-delete) but it is nobody's live checklist.
//   active    — a live, unfinished checklist. THE default question.
//   completed — finished onboarding, and not archived afterwards.
// `active` and `completed` are mutually exclusive and neither includes an
// archived record, so "active + completed" is the whole non-archived world.
//
// ⚠ Keep in sync with functions/src/util/onboardingInstance.js (server twin).

/** Staff unassigned this hire — the record is kept, but it is not live. */
export function isArchivedInstance(instance) {
  return !!instance && instance.archived === true;
}

/** A live, unfinished checklist — the predicate almost every caller wants. */
export function isActiveInstance(instance) {
  return !!instance && !isArchivedInstance(instance) && instance.status !== 'complete';
}

/** Finished onboarding and still on the books (not archived afterwards). */
export function isCompletedInstance(instance) {
  return !!instance && !isArchivedInstance(instance) && instance.status === 'complete';
}

// Per-item runtime fields appended to every flattened template item. Matches
// the full shape the admin pipeline + agent checklist both expect.
export const ITEM_RUNTIME_DEFAULTS = {
  status: 'pending',
  completedAt: null,
  completedBy: null,
  blockedReason: null,
  blockedAt: null,
  staffNotes: [],
  uploadUrl: null,
  approvedBy: null,
  approvedAt: null,
  nudgesSent: 0,
  lastNudgeAt: null,
};

/**
 * Flatten a template's sections (+ optional office overrides) into a flat
 * items[] array with runtime defaults applied.
 *
 * @param {object} template      the onboardingTemplates doc data
 * @param {object} opts
 * @param {string} [opts.office]               office key for officeOverrides
 * @param {boolean} [opts.fallbackToPhases]    use template.phases if no sections (self-service parity)
 * @param {boolean} [opts.injectSectionId]     set item.sectionId = section.id (pipeline parity)
 * @param {boolean} [opts.mergeOfficeOverrides] append template.officeOverrides[office] (pipeline parity)
 * @returns {Array} flattened items
 */
export function flattenTemplateItems(template, {
  office = '',
  fallbackToPhases = false,
  injectSectionId = false,
  mergeOfficeOverrides = false,
} = {}) {
  // RW-1668: instances are built from the last PUBLISHED snapshot — the
  // editor's autosaved draft (`sections`) never reaches a hire until publish.
  // Docs predating the publish machinery have no `published` field, so their
  // raw sections/phases stay live exactly as before.
  const published = template && template.published && Array.isArray(template.published.sections)
    ? template.published.sections
    : null;
  const sections = published
    || (template && template.sections)
    || (fallbackToPhases && template && template.phases)
    || [];

  const items = [];
  sections.forEach((section) => {
    (section.items || []).forEach((item) => {
      items.push({
        ...item,
        ...(injectSectionId ? { sectionId: section.id } : {}),
        ...ITEM_RUNTIME_DEFAULTS,
        // RW-774: carry per-item action-plan automations template→instance (snapshot).
        actions: item.actions || [],
      });
    });
  });

  if (mergeOfficeOverrides) {
    // RW-1632 (T2-02/T2-08/T2-14): canonical-key lookup, array-shape guard,
    // and provenance tag — see the server twin's override branch.
    const overrides = officeOverridesFor(template, office);
    overrides.forEach((ov) => {
      items.push({
        ...ov.item,
        sectionId: ov.sectionId,
        officeOverrideOf: canonicalOfficeKey(office),
        ...ITEM_RUNTIME_DEFAULTS,
        actions: (ov.item && ov.item.actions) || [],
      });
    });
  }

  return items;
}

/**
 * Pick the onboarding template for a user from the role→templateId map. The
 * user's FIRST onboarding-eligible role wins (v1 maps only `agent`); the
 * onboardingInstanceId pointer + agent checklist are single-instance, so we
 * never assign more than one. Returns { templateId, role } or null.
 */
export function selectMappedTemplate(roles, roleTemplates) {
  const rt = roleTemplates || {};
  const list = Array.isArray(roles) ? roles : [];
  for (const r of list) {
    if (rt[r]) return { templateId: rt[r], role: r };
  }
  return null;
}

/**
 * Pure decision for the RW-697 auto-assign step. Takes already-resolved facts
 * (so it has NO knowledge of the role-claim bail — the decision can never be
 * short-circuited by it) and returns { action: 'create'|'skip', reason?, ... }.
 *
 * Order matters: tenant → idempotency → config → mapping → template → tenant
 * isolation → create.
 */
export function planAutoAssign({ tenantId, hasExistingInstance, configExists, selected, template } = {}) {
  if (!tenantId) return { action: 'skip', reason: 'no-tenant' };
  if (hasExistingInstance) return { action: 'skip', reason: 'existing-instance' };
  if (!configExists) return { action: 'skip', reason: 'no-config' };
  if (!selected || !selected.templateId) return { action: 'skip', reason: 'no-mapped-role' };
  if (!template) return { action: 'skip', reason: 'template-missing' };
  if (template.tenantId && template.tenantId !== tenantId) return { action: 'skip', reason: 'tenant-mismatch' };
  return { action: 'create', templateId: selected.templateId, role: selected.role };
}

// Reduced per-item shape used only inside the self-service variant's `phases`
// mirror (OnboardingFlow wrote a 3-field runtime shape there, not the full 10).
function reducedPhaseItem(it) {
  return { ...it, status: 'pending', completedAt: null, completedBy: null };
}

/**
 * Build the full instance payload (and flattened items) for a given variant.
 *
 * Returns { items, totalItems, requiredItems, payload }. The caller wraps
 * `payload` with withTenant()/tenantId stamping and performs the actual write
 * + side effects (the SDK differs per call site).
 *
 * @param {object} args
 * @param {('pipeline'|'self-service'|'auto-provision')} args.variant
 * @param {object} args.template            onboardingTemplates doc data
 * @param {string} args.agentUid
 * @param {string} args.agentName
 * @param {string} args.agentEmail
 * @param {string} [args.agentPhoto]
 * @param {string} [args.office]
 * @param {string} args.createdBy
 * @param {object|null} [args.mentor]        { uid, name } (pipeline)
 * @param {object|null} [args.trainer]       { uid, name } (pipeline)
 * @param {*} args.serverTime                serverTimestamp() sentinel for startedAt/createdAt
 * @param {*} [args.templateVersion]         resolved templateVersion value (pipeline)
 * @param {*} [args.targetDate]              resolved Timestamp|null (pipeline)
 */
export function buildOnboardingInstance(args) {
  const {
    variant,
    template,
    agentUid,
    agentName,
    agentEmail,
    agentPhoto = '',
    office = '',
    createdBy,
    mentor = null,
    trainer = null,
    serverTime,
    templateVersion,
    targetDate = null,
  } = args;

  if (variant === 'self-service') {
    // OnboardingFlow parity: sections||phases source, no explicit sectionId,
    // no office overrides; includes a `phases` mirror with reduced item shape.
    const items = flattenTemplateItems(template, { fallbackToPhases: true });
    const sections = (template && template.sections) || (template && template.phases) || [];
    const payload = {
      agentUid,
      agentName,
      agentEmail,
      agentOffice: office,
      templateId: template.id,
      templateName: template.name,
      status: 'in_progress',
      stage: 'new',
      startedAt: serverTime,
      createdAt: serverTime,
      createdBy,
      completedAt: null,
      completedBy: null,
      notes: '',
      mentorUid: null,
      mentorName: null,
      phases: sections.map((s) => ({
        ...s,
        items: (s.items || []).map(reducedPhaseItem),
      })),
      items,
    };
    return { items, totalItems: items.length, requiredItems: items.filter((i) => i.required).length, payload };
  }

  if (variant === 'auto-provision') {
    // Derived from the agent OnboardingChecklist reader requirements + admin
    // view extras + kept side-effect contract. Full item runtime shape so the
    // checklist and admin pipeline both render correctly.
    const items = flattenTemplateItems(template, {
      office,
      injectSectionId: true,
      mergeOfficeOverrides: true,
    });
    const totalItems = items.length;
    const requiredItems = items.filter((i) => i.required).length;
    const payload = {
      agentUid,
      agentName,
      agentEmail,
      agentPhoto,
      office,
      templateId: template.id,
      templateName: template.name,
      status: 'new',
      stage: 'new',
      startedAt: serverTime,
      completedAt: null,
      // RW-726: the provisioning seam now passes a real live date (null when a
      // caller omits it) — the pipeline variant has always carried this key.
      targetDate,
      createdBy,
      mentorUid: null,
      mentorName: null,
      trainerUid: null,
      trainerName: null,
      totalItems,
      completedItems: 0,
      requiredItems,
      completedRequiredItems: 0,
      items,
      emailsSent: [],
      // RW-774: instance-level "when all complete" action-plan automations (snapshot).
      instanceActions: (template && template.instanceActions) || [],
    };
    return { items, totalItems, requiredItems, payload };
  }

  // Default: 'pipeline' — admin "Start Onboarding" modal parity.
  const items = flattenTemplateItems(template, {
    office,
    injectSectionId: true,
    mergeOfficeOverrides: true,
  });
  const totalItems = items.length;
  const requiredItems = items.filter((i) => i.required).length;
  const payload = {
    agentUid,
    agentName,
    agentEmail,
    agentPhoto,
    office,
    templateId: template.id,
    templateName: template.name,
    templateVersion,
    status: 'new',
    stage: 'new',
    startedAt: serverTime,
    completedAt: null,
    targetDate,
    createdBy,
    mentorUid: mentor ? mentor.uid : null,
    mentorName: mentor ? mentor.name : null,
    trainerUid: trainer ? trainer.uid : null,
    trainerName: trainer ? trainer.name : null,
    totalItems,
    completedItems: 0,
    requiredItems,
    completedRequiredItems: 0,
    items,
    emailsSent: [],
    // RW-774: instance-level "when all complete" action-plan automations (snapshot).
    instanceActions: (template && template.instanceActions) || [],
  };
  return { items, totalItems, requiredItems, payload };
}

// ─── Restart / reset (RW-763) ────────────────────────────────────────────────

/**
 * Per-item progress/runtime fields cleared by a full reset. Mirrors
 * ITEM_RUNTIME_DEFAULTS (the fresh-instance shape) so a reset item is
 * indistinguishable from a freshly-provisioned one — with two deliberate
 * differences:
 *   - human-authored `staffNotes` are intentionally PRESERVED (admin
 *     annotations, not agent progress) — this object simply doesn't touch them;
 *   - it additionally nulls the lifecycle fields toggleOnboardingItemStatus can
 *     set that ITEM_RUNTIME_DEFAULTS predates (acknowledge / manual-complete /
 *     document-upload pointers), and clears the cron-set escalation (RW-770) so
 *     a reset item never carries a stale ⚠ from its prior life.
 * Storage OBJECTS are never deleted (Rule 9) — only the instance's pointers are
 * reset; the agent re-completes the item.
 */
export const RESET_ITEM_PROGRESS = {
  status: 'pending',
  completedAt: null,
  completedBy: null,
  completedManually: null,
  approvedBy: null,
  approvedAt: null,
  acknowledgedAt: null,
  blockedReason: null,
  blockedAt: null,
  escalated: false,
  escalatedAt: null,
  nudgesSent: 0,
  lastNudgeAt: null,
  // RW-1631 T2-16 (kept through the RW-1679 legacy-cron delete): the retired
  // daily cron wrote the bookkeeping twins `nudgeCount`/`lastNudgedAt` and
  // readers (e.g. OnboardingTaskRow's "last nudged" line) still fall back to
  // them — clear both so a full reset can't resurrect pre-reset nudge history.
  nudgeCount: 0,
  lastNudgedAt: null,
  uploadUrl: null,
  storagePath: null,
  documentName: null,
  documentUploadedAt: null,
  documentUploadedBy: null,
  documentUploadedByName: null,
};

/**
 * RW-1727 — un-tick a task's steps without destroying them.
 *
 * A subtask's `done` flag is progress ("wipe all checklist progress" — the
 * confirm dialog's own words), but the step LIST is structure: authored by a
 * template or by staff, and not the agent's to lose on a restart. So a reset
 * clears the flags and keeps the steps, their ids, titles and order.
 *
 * This cannot live in RESET_ITEM_PROGRESS: that object is SPREAD over the item,
 * so any static `subtasks` value there would replace the array wholesale rather
 * than map it. Items with no step list keep having none — the field is only
 * written when one is already there.
 */
export function resetItemSubtasks(subtasks) {
  if (!Array.isArray(subtasks)) return [];
  return subtasks.map((s) => ((s && typeof s === 'object') ? { ...s, done: false } : s));
}

/**
 * Pure planner for the RW-763 restartOnboarding callable. Given an instance doc
 * and a mode, returns the field updates to apply (instance + user). NO firebase
 * imports — the caller injects the serverTimestamp() sentinel and runs the write
 * inside a transaction.
 *
 *   - 'relaunch_wizard' — re-show the first-login profile wizard. Instance items
 *     are untouched; stamps restart audit fields only. User flags onboarding +
 *     profile incomplete so the wizard re-appears.
 *   - 'full_reset'      — every item → fresh 'pending' (RESET_ITEM_PROGRESS)
 *     with its RW-795 steps un-ticked (resetItemSubtasks), instance
 *     status/stage → 'new', denormalized counts zeroed, user
 *     onboardingComplete → false. profileComplete is left as-is (use
 *     'relaunch_wizard' to re-trigger the profile wizard).
 *
 * @returns {{ mode: string, touchedItems: boolean, instanceUpdate: object, userUpdate: object }}
 */
export function planOnboardingReset(instance, mode, { serverTime = null, actorUid = null } = {}) {
  const safe = instance || {};

  if (mode === 'relaunch_wizard') {
    return {
      mode,
      touchedItems: false,
      instanceUpdate: {
        restartedAt: serverTime,
        restartedBy: actorUid,
        lastActivityAt: serverTime,
      },
      userUpdate: { onboardingComplete: false, profileComplete: false },
    };
  }

  if (mode === 'full_reset') {
    const items = Array.isArray(safe.items) ? safe.items : [];
    const resetItems = items.map((it) => {
      const next = { ...it, ...RESET_ITEM_PROGRESS };
      // RW-1727: only when the item actually carries steps — never invent an
      // empty list on an item that never had one.
      if (Array.isArray(it.subtasks)) next.subtasks = resetItemSubtasks(it.subtasks);
      return next;
    });
    const requiredItems = resetItems.filter((i) => i.required).length;
    return {
      mode,
      touchedItems: true,
      instanceUpdate: {
        items: resetItems,
        status: 'new',
        stage: 'new',
        completedAt: null,
        completedBy: null,
        totalItems: resetItems.length,
        completedItems: 0,
        requiredItems,
        completedRequiredItems: 0,
        restartedAt: serverTime,
        restartedBy: actorUid,
        lastActivityAt: serverTime,
      },
      userUpdate: { onboardingComplete: false },
    };
  }

  throw new Error(`planOnboardingReset: unknown mode "${mode}"`);
}
